import { animate, cleanInlineStyles, createDrawable, cubicBezier, stagger } from 'animejs';

/**
 * Único ponto de uso da anime.js. Movimento do sistema é contido: sem bounce e sem loop
 * (design-system/styles/tokens.css). Durações em ms, iguais aos tokens --tc-duration-*.
 */
export const MOTION_DURATION = { fast: 150, base: 240, slow: 420, journey: 1100 };

const EASE_OUT = cubicBezier(0.2, 0, 0, 1);
const STAGGER_STEP = 45;
const ENTER_OFFSET = 8;

/** Sem a API (ambiente de teste, navegador antigo) vale o estado final, sem animação. */
export function prefersReducedMotion() {
  return typeof window.matchMedia !== 'function' || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Elementos entram em cascata. Ao terminar, sai o estilo inline para não atrapalhar sticky e layout. */
export function enterInSequence(targets) {
  if (prefersReducedMotion() || !targets?.length) return null;

  return animate(targets, {
    opacity: [0, 1],
    translateY: [ENTER_OFFSET, 0],
    duration: MOTION_DURATION.base,
    delay: stagger(STAGGER_STEP),
    ease: EASE_OUT,
    onComplete: (animation) => cleanInlineStyles(animation)
  });
}

/** Leva um número de `from` até `to`. O último valor é sempre `to`, exato. */
export function tweenNumber({ from, to, duration = MOTION_DURATION.slow, onUpdate, onComplete }) {
  if (prefersReducedMotion() || from === to) {
    onUpdate(to);
    onComplete?.();

    return null;
  }

  const state = { value: from };

  return animate(state, {
    value: to,
    duration,
    ease: EASE_OUT,
    onUpdate: () => onUpdate(state.value),
    onComplete: () => {
      onUpdate(to);
      onComplete?.();
    }
  });
}

/** Desenha um traço SVG do início ao fim. */
export function drawStroke(elements, { duration = MOTION_DURATION.slow, delay = 0 } = {}) {
  if (prefersReducedMotion() || !elements?.length) return null;

  return animate(createDrawable(elements), { draw: ['0 0', '0 1'], duration, delay, ease: EASE_OUT });
}
